import { useTranslation } from 'react-i18next';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import FundFilter from '@/components/filters/FundFilter/FundFilter';
import { OptionsRangeFilter } from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter';
import styles from '@/components/filters/FiltersArea.module.scss';
import { ActivityFilter, ASSIGNABLE_ROLES, NO_ROLE_FILTER_ID } from './userHelpers';

const ACTIVITY_OPTIONS: ActivityFilter[] = ['all', 'active30', 'active90', 'never'];

interface UsersFiltersAreaProps {
  selectedRoles: string[];
  onRolesChange: (roles: string[]) => void;
  selectedFundId: number | null;
  onFundChange: (fundId: number | null) => void;
  activityFilter: ActivityFilter;
  onActivityChange: (filter: ActivityFilter) => void;
  roleCounts: Record<string, number>;
  onResetFilters: () => void;
  hasActiveFilters: boolean;
}

const UsersFiltersArea = ({
  selectedRoles,
  onRolesChange,
  selectedFundId,
  onFundChange,
  activityFilter,
  onActivityChange,
  roleCounts,
  onResetFilters,
  hasActiveFilters,
}: UsersFiltersAreaProps) => {
  const { t } = useTranslation();

  const toggleRole = (roleId: string) => {
    onRolesChange(selectedRoles.includes(roleId) ? selectedRoles.filter((r) => r !== roleId) : [...selectedRoles, roleId]);
  };

  return (
    <div className={styles.filtersArea}>
      <div className={styles.filtersContainer}>
        {[...ASSIGNABLE_ROLES, NO_ROLE_FILTER_ID].map((roleId) => (
          <StatusFilter
            key={roleId}
            title={t(`usersManagement.roles.${roleId}`)}
            isSelected={selectedRoles.includes(roleId)}
            onClick={() => toggleRole(roleId)}
            count={roleCounts[roleId] ?? 0}
          />
        ))}
        <span className={styles.statusDivider} />
        <FundFilter selectedFundId={selectedFundId} onFundChange={onFundChange} />
        <OptionsRangeFilter
          title="usersManagement.filters.activity"
          filterType="options"
          selectionType="radio"
          options={ACTIVITY_OPTIONS.map((option) => ({
            id: option,
            label: `usersManagement.filters.${option}`,
            // "all" is the neutral state, so it never lights the filter button up.
            checked: option !== 'all' && activityFilter === option,
          }))}
          onOptionChange={(id) => onActivityChange(id as ActivityFilter)}
        />
        {hasActiveFilters && (
          <button className="body-sm-link text-(--font-link)" onClick={onResetFilters}>
            {t('resetFilters')}
          </button>
        )}
      </div>
    </div>
  );
};

export default UsersFiltersArea;
